import { forwardRef, type ButtonHTMLAttributes, type ElementType, type KeyboardEvent, type ReactNode } from "react";

import { cn } from "../lib/cn";
import { INSET_GROUP_CLASS } from "../lib/inset";
import { Tooltip } from "./Tooltip";

export interface ToolbarProps {
  /** Names the group of controls for assistive technology. */
  label: string;
  children: ReactNode;
  className?: string;
}

/** Arrow keys move between the toolbar's enabled buttons; Tab leaves the toolbar. */
function moveFocus(event: KeyboardEvent<HTMLDivElement>) {
  const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
  const edge = event.key === "Home" ? "first" : event.key === "End" ? "last" : null;
  if (step === 0 && !edge) return;
  const items = Array.from(
    event.currentTarget.querySelectorAll<HTMLButtonElement>("[data-toolbar-item]:not(:disabled)"),
  );
  if (items.length === 0) return;
  event.preventDefault();
  const current = items.indexOf(document.activeElement as HTMLButtonElement);
  const next = edge === "first"
    ? 0
    : edge === "last"
      ? items.length - 1
      : (Math.max(0, current) + step + items.length) % items.length;
  items[next].focus();
}

/**
 * A compact, inset group of actions. It shares the inset look of the segmented
 * control, so a bar holding both reads as one set of tools rather than a row
 * of unrelated buttons.
 */
export function Toolbar({ label, children, className }: ToolbarProps) {
  return (
    <div
      role="toolbar"
      aria-label={label}
      onKeyDown={moveFocus}
      className={cn(
        "density-control-lg inline-flex items-center gap-0.5 rounded-control p-1",
        INSET_GROUP_CLASS,
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface ToolbarButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon: ElementType;
  /** Always the accessible name. Shown as text when `showLabel`, otherwise in a tooltip. */
  label: string;
  showLabel?: boolean;
  /** A keyboard shortcut shown in the tooltip. */
  shortcut?: string;
  /** Tooltip text when it should say more than the label. */
  hint?: string;
}

export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(function ToolbarButton(
  { icon: Icon, label, showLabel = false, shortcut, hint, className, type = "button", ...props },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type={type}
      data-toolbar-item=""
      aria-label={showLabel ? undefined : label}
      className={cn(
        "density-control-sm inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md text-xs font-semibold text-secondary transition-colors",
        "hover:bg-surface hover:text-primary",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
        "disabled:pointer-events-none disabled:opacity-40",
        "aria-pressed:bg-surface aria-pressed:text-interaction-text",
        showLabel ? "px-2.5" : "density-square-sm",
        className,
      )}
      {...props}
    >
      <Icon className="size-4 shrink-0" strokeWidth={1.8} aria-hidden />
      {showLabel ? label : null}
    </button>
  );
  return showLabel && !shortcut && !hint ? button : (
    <Tooltip content={hint ?? label} shortcut={shortcut}>
      {button}
    </Tooltip>
  );
});

/** A thin divider between groups of related toolbar actions. */
export function ToolbarSeparator() {
  return <span role="separator" aria-orientation="vertical" className="mx-1 h-4 w-px shrink-0 bg-border" />;
}
